import { cssVar, forwardRef, mapResponsive, useTheme } from '@hover/blueprint';
import type {
  ResponsiveValue,
  SkeletonProps as SystemSkeletonProps,
} from '@hover/blueprint/chakra';
import { Skeleton as SystemSkeleton } from '@hover/blueprint/chakra';
import React from 'react';

export interface SkeletonProps
  extends Omit<SystemSkeletonProps, 'colorScheme'> {
  colorScheme?: ResponsiveValue<'light' | 'dark'>;
}

export const $startColor = cssVar('skeleton-start-color');
export const $endColor = cssVar('skeleton-end-color');

/**
 * Skeleton with styles for the homeowner app
 *
 * @todo(design-system): move this into the design system.
 *
 * @remarks note that while `Skeleton` is fully themeable, in order to support a
 * responsive `colorScheme` we'll need to compose the Chakra `Skeleton` like
 * this. Also, this currently depends on the `--skeleton-*-color` CSS variables
 * defined in Chakra's default theme so even if we do move this whole component,
 * we'll still probably want to add our own theme to prevent breakage on the CSS
 * variable names.
 */
const Skeleton = forwardRef<SkeletonProps, 'div'>(
  ({ colorScheme = 'dark', sx, ...props }, ref) => {
    const {
      colors: { lightAlpha, darkAlpha },
    } = useTheme();

    return (
      <SystemSkeleton
        borderRadius="200"
        sx={{
          ...sx,
          [$startColor.variable]: mapResponsive(
            c => (c === 'light' ? lightAlpha[200] : darkAlpha[300]),
            colorScheme,
          ),
          [$endColor.variable]: mapResponsive(
            c => (c === 'light' ? lightAlpha[100] : darkAlpha[100]),
            colorScheme,
          ),
        }}
        {...props}
        ref={ref}
      />
    );
  },
);

export { Skeleton };
